import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Lock,
  Timer,
  ExitToApp,
  CheckCircle,
  Reviews,
  Quiz,
  School,
  EmojiEvents,
  PlayArrow,
  Check,
} from "@mui/icons-material";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useStudentClassroom } from "../../context/StudentClassroomContext";
import { useConfirmDialog } from "./../../components/shared/ConfirmDialog";
import ModuleContentLoader, {
  type ModuleConfig,
  type LoadedSection,
} from "../../services/ModuleContentLoader";
import QuizStartScreen from "../../components/quiz/QuizStartScreen";

// Import progress tracking services
import { learningStorageService } from "../../services/learningStorageService";
import learningProgressService from "../../services/learningprogressservice";

const DocumentContainer = styled(Paper)(({ theme }) => ({
  maxWidth: "1200px",
  margin: "0 auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  height: "calc(100vh - 128px)",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    borderRadius: "4px",
  },
}));

const DocumentHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#F8F9FA",
  borderBottom: "1px solid #E9ECEF",
  padding: "12px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "48px",
  [theme.breakpoints.down("md")]: {
    padding: "8px 16px",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  color: "#495057",
  margin: 0,
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    width: "100%",
    justifyContent: "space-between",
  },
}));

const DocumentContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const DocumentSidebar = styled(Box)(({ theme }) => ({
  width: "280px",
  backgroundColor: "#F8F9FA",
  borderRight: "1px solid #E9ECEF",
  padding: "16px",
  overflowY: "auto",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    borderRight: "none",
    borderBottom: "1px solid #E9ECEF",
    maxHeight: "200px",
  },
}));

const DocumentMain = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  backgroundColor: "#FFFFFF",
}));

const ContentArea = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "32px",
  left: "32px",
  right: "32px",
  bottom: "32px",
  [theme.breakpoints.down("md")]: {
    top: "16px",
    left: "16px",
    right: "16px",
    bottom: "16px",
  },
}));

const ModuleNavItem = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isActive" && prop !== "isCompleted" && prop !== "isLocked",
})<{ isActive?: boolean; isCompleted?: boolean; isLocked?: boolean }>(
  ({ theme, isActive, isCompleted, isLocked }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    margin: "4px 0",
    borderRadius: "6px",
    cursor: isLocked ? "not-allowed" : "pointer",
    fontSize: "14px",
    transition: "all 0.2s ease",
    backgroundColor: isActive ? "#E3F2FD" : "transparent",
    color: isLocked
      ? "#B0BEC5"
      : isActive
      ? "#1976D2"
      : isCompleted
      ? "#2E7D32"
      : "#495057",
    border: isActive ? "1px solid #2196F3" : "1px solid transparent",
    opacity: isLocked ? 0.5 : 1,
    "&:hover": {
      backgroundColor: isLocked
        ? "transparent"
        : isActive
        ? "#E3F2FD"
        : "#F5F5F5",
    },
  })
);

const ModuleNumber = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isActive" && prop !== "isCompleted" && prop !== "isLocked",
})<{ isActive?: boolean; isCompleted?: boolean; isLocked?: boolean }>(
  ({ theme, isActive, isCompleted, isLocked }) => ({
    width: "24px",
    height: "24px",
    minWidth: "24px",
    minHeight: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 600,
    backgroundColor: isLocked
      ? "#E0E0E0"
      : isActive
      ? "#2196F3"
      : isCompleted
      ? "#4CAF50"
      : "#E9ECEF",
    color: isLocked ? "#9E9E9E" : isActive || isCompleted ? "white" : "#6C757D",
  })
);

interface ModuleContext {
  program: string;
  week: string;
  moduleType: string;
  displayName: string;
}

// Parse moduleId to extract program, week, and module type
// moduleType parameter (from navigation state) takes priority over the hardcoded map
const parseModuleContext = (
  moduleId: string,
  moduleType?: string
): ModuleContext | null => {
  try {
    // Handle EAP module format: "eap_yellow_week1_module1"
    if (moduleId.includes("eap_")) {
      const parts = moduleId.split("_");
      if (parts.length >= 4) {
        const program = parts[1]; // 'yellow'
        const weekPart = parts[2]; // 'week1'
        const modulePart = parts[3]; // 'module1'

        const weekNumber = weekPart.replace("week", "");

        // Use moduleType from navigation state if available;
        // otherwise fall back to hardcoded map
        let resolvedType = moduleType?.toLowerCase();
        if (!resolvedType) {
          const moduleNumber = parseInt(modulePart.replace("module", ""));
          const moduleTypeMap: { [key: number]: string } = {
            1: "reading",
            2: "listening",
            3: "speaking",
            4: "writing",
            5: "grammar",
            6: "vocabulary",
            7: "review",
          };
          resolvedType = moduleTypeMap[moduleNumber] || "reading";
        }

        return {
          program: program,
          week: `week${weekNumber}`,
          moduleType: resolvedType,
          displayName: `EAP ${program.toUpperCase()} - Week ${weekNumber} - ${
            resolvedType.charAt(0).toUpperCase() + resolvedType.slice(1)
          }`,
        };
      }
    }

    // Fallback parsing for other formats
    const parts = moduleId.split("_");
    if (parts.length >= 3) {
      const resolvedType = moduleType?.toLowerCase() || parts[2] || "reading";
      return {
        program: parts[0],
        week: parts[1],
        moduleType: resolvedType,
        displayName: `${parts[0].toUpperCase()} - ${parts[1]} - ${
          resolvedType.charAt(0).toUpperCase() + resolvedType.slice(1)
        }`,
      };
    }

    return null;
  } catch (error) {
    console.error("Failed to parse moduleId:", moduleId, error);
    return null;
  }
};

// Content version for localStorage validation
const CONTENT_VERSION = "v1.0";

// Main ModulePage component
const ModulePage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    currentProgram,
    currentWeek,
    currentModule,
    setCurrentProgram,
    setCurrentWeek,
    setCurrentModule,
    updateModuleProgress,
  } = useStudentClassroom();
  const { showConfirm, ConfirmDialog } = useConfirmDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Dynamic state
  const [moduleContext, setModuleContext] = useState<ModuleContext | null>(
    null
  );
  const [moduleConfig, setModuleConfig] = useState<ModuleConfig | null>(null);
  const [sections, setSections] = useState<LoadedSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Section progress state
  const [completedSections, setCompletedSections] = useState<Set<string>>(
    new Set()
  );
  const [currentSection, setCurrentSection] = useState<string>("");
  const [currentSubPageIndex, setCurrentSubPageIndex] = useState<number>(0);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizResults, setQuizResults] = useState<any>(null);

  // Saving state for API calls
  const [isSaving, setIsSaving] = useState(false);

  // ============================================================
  // localStorage Progress Tracking
  // ============================================================

  /**
   * Save current progress to localStorage
   */
  const saveProgressToLocalStorage = useCallback(
    (
      sectionIndex: number,
      sectionId: string,
      subPageIndex: number,
      completedSectionIndices: number[]
    ) => {
      if (!moduleId || sections.length === 0) return;

      learningStorageService.saveLearningProgress({
        moduleId,
        currentSectionIndex: sectionIndex,
        currentSectionId: sectionId,
        currentSubPageIndex: subPageIndex,
        sectionsCompleted: completedSectionIndices,
        totalSections: sections.length,
        lastUpdate: Date.now(),
        contentVersion: CONTENT_VERSION,
        startedAt:
          learningStorageService.loadLearningProgress(moduleId)?.startedAt ||
          Date.now(),
      });
    },
    [moduleId, sections.length]
  );

  /**
   * Convert section IDs to indices for localStorage
   */
  const getCompletedSectionIndices = useCallback((): number[] => {
    const indices: number[] = [];
    completedSections.forEach((sectionId) => {
      const index = sections.findIndex((s) => s.id === sectionId);
      if (index !== -1) {
        indices.push(index);
      }
    });
    return indices.sort((a, b) => a - b);
  }, [completedSections, sections]);

  /**
   * Initialize progress from localStorage or start fresh
   */
  const initializeProgressFromStorage = useCallback(
    (loadedSections: LoadedSection[]) => {
      if (!moduleId) return;

      // ✅ Check if learning content is already completed (review mode)
      const isReviewMode = location.state?.isReviewMode === true;
      const learningCompleted =
        location.state?.learningContentCompleted === true;

      if (isReviewMode || learningCompleted) {
        console.log("📖 Review mode - unlocking all sections");
        setCurrentSection(loadedSections[0].id);
        setCurrentSubPageIndex(0);
        // Unlock ALL sections for review
        setCompletedSections(new Set(loadedSections.map((s) => s.id)));
        return;
      }

      // Convert sections to format expected by validation
      const sectionsForValidation = loadedSections.map((s) => ({
        id: s.id,
      }));

      // Validate and get saved position
      const { sectionIndex, subPageIndex, isValid } =
        learningStorageService.validateAndGetPosition(
          moduleId,
          sectionsForValidation
        );

      if (isValid) {
        // Restore saved progress
        const savedProgress =
          learningStorageService.loadLearningProgress(moduleId);
        console.log("📂 Restoring saved progress:", savedProgress);

        // Set current position
        setCurrentSection(loadedSections[sectionIndex].id);
        setCurrentSubPageIndex(subPageIndex);

        // Restore completed sections
        if (savedProgress?.sectionsCompleted) {
          const completedIds = new Set<string>();
          savedProgress.sectionsCompleted.forEach((index) => {
            if (loadedSections[index]) {
              completedIds.add(loadedSections[index].id);
            }
          });
          setCompletedSections(completedIds);
        }
      } else {
        // Start fresh - initialize localStorage
        console.log("📝 Starting fresh - initializing progress");
        setCurrentSection(loadedSections[0].id);
        setCurrentSubPageIndex(0);
        setCompletedSections(new Set());

        learningStorageService.initializeLearningProgress(
          moduleId,
          loadedSections.length,
          loadedSections[0].id,
          CONTENT_VERSION
        );
      }
    },
    [moduleId, location.state]
  );

  // ============================================================
  // Module Initialization
  // ============================================================

  useEffect(() => {
    const initializeModule = async () => {
      try {
        setLoading(true);
        setError(null);

        let context: ModuleContext | null = null;

        // Try to get context from URL params first
        // Use moduleType from navigation state to resolve correct content
        const stateModuleType = (location.state as any)?.moduleType;
        if (moduleId) {
          context = parseModuleContext(moduleId, stateModuleType);
          console.log("📝 Parsed from URL params:", { moduleId, stateModuleType, context });
        }

        // Fallback to StudentClassroomContext if URL parsing fails
        if (!context && currentProgram && currentWeek && currentModule) {
          context = {
            program: currentProgram,
            week: `week${currentWeek}`,
            moduleType: currentModule,
            displayName: `${currentProgram.toUpperCase()} - Week ${currentWeek} - ${
              currentModule.charAt(0).toUpperCase() + currentModule.slice(1)
            }`,
          };
          console.log("📝 Using context fallback:", context);
        }

        if (!context) {
          throw new Error(
            "Unable to determine module context from URL params or StudentClassroomContext"
          );
        }

        setModuleContext(context);

        // Update context state if we got context from URL
        if (moduleId) {
          setCurrentProgram(context.program);
          setCurrentWeek(parseInt(context.week.replace("week", "")));
          setCurrentModule(context.moduleType);
        }

        // Load module configuration
        console.log("🔄 Loading module config:", context);
        const config = await ModuleContentLoader.loadModuleConfig(
          context.program,
          context.week,
          context.moduleType
        );

        if (!config) {
          throw new Error(
            `Module configuration not found for ${context.program}/${context.week}/${context.moduleType}`
          );
        }

        console.log("📚 Loaded config:", config);
        setModuleConfig(config);

        // Load section components
        console.log("🔄 Loading section components:", config.sections);
        const loadedSections = await ModuleContentLoader.loadSectionComponents(
          config.sections
        );
        console.log("🎯 Loaded sections:", loadedSections);

        setSections(loadedSections);

        // Initialize progress from localStorage or start fresh
        if (loadedSections.length > 0) {
          initializeProgressFromStorage(loadedSections);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load module";
        console.error("❌ Module initialization error:", errorMessage, err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeModule();
  }, [
    moduleId,
    currentProgram,
    currentWeek,
    currentModule,
    setCurrentProgram,
    setCurrentWeek,
    setCurrentModule,
    initializeProgressFromStorage,
  ]);

  // ============================================================
  // Section Navigation Handlers
  // ============================================================

  const handleSectionComplete = useCallback(
    (sectionId: string) => {
      console.log(`✅ Section completed: ${sectionId}`);

      setCompletedSections((prev) => {
        const newCompleted = new Set([...Array.from(prev), sectionId]);

        // Save to localStorage
        const sectionIndex = sections.findIndex((s) => s.id === sectionId);
        const completedIndices = Array.from(newCompleted)
          .map((id) => sections.findIndex((s) => s.id === id))
          .filter((i) => i !== -1)
          .sort((a, b) => a - b);

        if (moduleId) {
          learningStorageService.markSectionCompleted(moduleId, sectionIndex);
        }

        return newCompleted;
      });
    },
    [moduleId, sections]
  );

  const handleGoToNextSection = useCallback(() => {
    const currentIndex = sections.findIndex((s) => s.id === currentSection);
    const nextSection = sections[currentIndex + 1];

    if (nextSection) {
      // Mark current section as complete
      handleSectionComplete(currentSection);

      // Move to next section
      setCurrentSection(nextSection.id);
      setCurrentSubPageIndex(0);

      // Save progress to localStorage
      if (moduleId) {
        learningStorageService.updateCurrentPosition(
          moduleId,
          currentIndex + 1,
          nextSection.id,
          0
        );
      }
    }
  }, [currentSection, sections, moduleId, handleSectionComplete]);

  const isSectionLocked = useCallback(
    (sectionId: string, index: number): boolean => {
      if (index === 0) return false;
      const previousSection = sections[index - 1];
      return !completedSections.has(previousSection.id);
    },
    [sections, completedSections]
  );

  const handleSectionClick = useCallback(
    (sectionId: string, index: number) => {
      if (!isSectionLocked(sectionId, index)) {
        setCurrentSection(sectionId);
        setCurrentSubPageIndex(0);

        // Save position to localStorage
        if (moduleId) {
          learningStorageService.updateCurrentPosition(
            moduleId,
            index,
            sectionId,
            0
          );
        }
      }
    },
    [isSectionLocked, moduleId]
  );

  // ============================================================
  // Quiz Handlers
  // ============================================================

  const handleTakeQuiz = useCallback(async () => {
    if (!moduleId || !moduleContext) return;

    console.log("🎯 Starting final quiz for module:", moduleContext);

    setIsSaving(true);

    try {
      // Call API to mark learning content as completed
      const weekNumber = parseInt(moduleContext.week.replace("week", ""));
      const result = await learningProgressService.markLearningContentCompleted(
        weekNumber,
        moduleId
      );

      if (result.success) {
        console.log("✅ Learning content marked as complete in backend");

        // Clear localStorage progress (learning content is done)
        learningStorageService.removeLearningProgress(moduleId);
        console.log("🗑️ Cleared localStorage progress");

        // Show quiz
        setShowQuiz(true);
      } else {
        console.error("❌ Failed to mark learning complete:", result.message);
        // Still allow quiz but show warning
        showConfirm({
          title: "Warning",
          message:
            "Could not save your learning progress. You can still take the quiz, but your progress may not be recorded.",
          actions: [
            {
              label: "Continue to Quiz",
              variant: "contained",
              onClick: () => setShowQuiz(true),
            },
            {
              label: "Cancel",
              variant: "outlined",
              onClick: () => {},
            },
          ],
        });
      }
    } catch (error) {
      console.error("❌ Error marking learning complete:", error);
      // Allow quiz anyway
      setShowQuiz(true);
    } finally {
      setIsSaving(false);
    }
  }, [moduleId, moduleContext, showConfirm]);

  const handleQuizComplete = useCallback(
    (results: any) => {
      console.log("📊 Quiz completed with results:", results);

      // Extract data from nested response structure
      const quizData = results.data;

      setQuizResults(quizData.score);
      setShowQuiz(false);

      // Update module progress in context with quiz completion
      if (moduleContext && currentModule) {
        const moduleProgress = {
          moduleId: currentModule,
          moduleName: `${
            moduleContext.moduleType.charAt(0).toUpperCase() +
            moduleContext.moduleType.slice(1)
          } Module`,
          weekNumber: currentWeek,
          completionPercentage: 100,
          sectionsCompleted: sections.map((s) => s.id),
          quizCompleted: true,
          quizScore: quizData.score,
          lastAccessed: Date.now(),
        };
        updateModuleProgress(currentModule, moduleProgress);
      }

      // Determine pass/fail status
      const isPassing = quizData.score >= 70;
      const statusEmoji = isPassing ? "🎉" : "📚";
      const statusTitle = isPassing ? "Module Complete!" : "Quiz Completed";

      // Show completion celebration
      showConfirm({
        title: `${statusEmoji} ${statusTitle}`,
        customIcon: (
          <EmojiEvents
            sx={{
              color: isPassing ? "success.main" : "warning.main",
              fontSize: 32,
            }}
          />
        ),
        message: (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {isPassing
                ? `Outstanding! You've successfully completed the ${
                    moduleContext?.moduleType || "learning"
                  } module!`
                : `You've completed the ${
                    moduleContext?.moduleType || "learning"
                  } quiz. Review the feedback below to improve.`}
            </Typography>

            {/* Main Stats Box */}
            <Box
              sx={{
                bgcolor: isPassing ? "success.50" : "warning.50",
                p: 2,
                borderRadius: 2,
                mb: 2,
                border: "1px solid",
                borderColor: isPassing ? "success.200" : "warning.200",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isPassing ? "success.dark" : "warning.dark",
                }}
              >
                ✅ Quiz Score: {Math.round(quizData.score)}%
              </Typography>
              <Typography
                variant="body2"
                color={isPassing ? "success.dark" : "warning.dark"}
              >
                ✓ Correct Answers: {quizData.correctAnswers}/
                {quizData.totalQuestions}
              </Typography>
              <Typography
                variant="body2"
                color={isPassing ? "success.dark" : "warning.dark"}
              >
                🎯 Points Earned: {quizData.earnedPoints}/{quizData.totalPoints}
              </Typography>
              <Typography
                variant="body2"
                color={isPassing ? "success.dark" : "warning.dark"}
              >
                ⏱️ Time Spent: {Math.round(quizData.timeSpent / 60000)} minutes
              </Typography>
              <Typography
                variant="body2"
                color={isPassing ? "success.dark" : "warning.dark"}
              >
                🏆 Module Status:{" "}
                {isPassing ? "Complete" : "Review Recommended"}
              </Typography>
            </Box>

            {/* Detailed Feedback Section */}
            {quizData.feedback && quizData.feedback.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  📋 Question Breakdown:
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                  {quizData.feedback.map((item: any, index: number) => (
                    <Box
                      key={item.questionId}
                      sx={{
                        mb: 1,
                        p: 1,
                        bgcolor: item.isCorrect ? "success.50" : "error.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: item.isCorrect
                          ? "success.200"
                          : "error.200",
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontWeight: 600 }}
                      >
                        Question {index + 1}:{" "}
                        {item.isCorrect ? "✅ Correct" : "❌ Incorrect"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Points: {item.earnedPoints}/{item.maxPoints}
                      </Typography>
                      {item.feedback && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          💡 {item.feedback}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              {isPassing
                ? "You can now proceed to the next module or review this content anytime."
                : "Consider reviewing the module content and retaking the quiz to improve your score."}
            </Typography>
          </Box>
        ),
        actions: [
          {
            label: "Return to My Classroom",
            variant: "contained",
            color: isPassing ? "primary" : "warning",
            startIcon: <PlayArrow />,
            onClick: () => {
              navigate("/student/my-classroom");
            },
          },
          {
            label: isPassing ? "Review Module" : "Review & Retake",
            variant: "outlined",
            startIcon: <Reviews />,
            onClick: () => {
              handleReviewModule();
            },
          },
        ],
        maxWidth: "md",
        disableBackdropClick: true,
        showCloseButton: false,
      });
    },
    [moduleContext, currentModule, currentWeek, sections, showConfirm, navigate]
  );

  const handleQuizExit = useCallback(() => {
    console.log("❌ Quiz exited");
    setShowQuiz(false);
  }, []);

  useEffect(() => {
    const shouldStartQuiz = searchParams.get("startQuiz") === "true";

    if (shouldStartQuiz && !loading && !showQuiz && moduleId) {
      console.log("🎯 Auto-starting quiz from URL parameter");
      setShowQuiz(true);

      // Clear the query param to prevent re-triggering on refresh
      navigate(`/student/my-classroom/module/${moduleId}`, {
        replace: true,
        state: location.state, // Preserve the state
      });
    }
  }, [searchParams, loading, showQuiz, moduleId, navigate, location.state]);

  // ============================================================
  // Module Review Handler
  // ============================================================

  const handleReviewModule = useCallback(() => {
    if (sections.length > 0) {
      setCurrentSection(sections[0].id);
      setCurrentSubPageIndex(0);
      setCompletedSections(new Set());

      // Re-initialize localStorage for review
      if (moduleId) {
        learningStorageService.initializeLearningProgress(
          moduleId,
          sections.length,
          sections[0].id,
          CONTENT_VERSION
        );
      }
    }
  }, [sections, moduleId]);

  // ============================================================
  // Module Completion Handler
  // ============================================================

  const handleCompleteModule = useCallback(() => {
    const allSectionsCompleted = completedSections.size === sections.length;
    const completionPercentage = Math.round(
      (completedSections.size / sections.length) * 100
    );

    if (!allSectionsCompleted) {
      // Show progress dialog if not all sections completed
      showConfirm({
        title: "Module Not Complete",
        customIcon: <School sx={{ color: "warning.main", fontSize: 28 }} />,
        message: (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You're making great progress! You've completed{" "}
              {completedSections.size} out of {sections.length} sections.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Progress: {completionPercentage}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Complete all sections to unlock the final quiz and earn your
              certificate.
            </Typography>
          </Box>
        ),
        actions: [
          {
            label: "Continue Learning",
            variant: "contained",
            startIcon: <PlayArrow />,
            onClick: () => {
              // Find next incomplete section
              const nextIncomplete = sections.find(
                (section) => !completedSections.has(section.id)
              );
              if (nextIncomplete) {
                const index = sections.findIndex(
                  (s) => s.id === nextIncomplete.id
                );
                setCurrentSection(nextIncomplete.id);
                setCurrentSubPageIndex(0);

                // Save to localStorage
                if (moduleId) {
                  learningStorageService.updateCurrentPosition(
                    moduleId,
                    index,
                    nextIncomplete.id,
                    0
                  );
                }
              }
            },
          },
        ],
        maxWidth: "sm",
      });
    } else {
      // Show completion celebration dialog
      showConfirm({
        title: "Module Completed!",
        customIcon: (
          <EmojiEvents sx={{ color: "success.main", fontSize: 32 }} />
        ),
        message: (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              🎉 Outstanding work! You've successfully completed all sections of
              the{" "}
              {moduleContext
                ? moduleContext.moduleType.charAt(0).toUpperCase() +
                  moduleContext.moduleType.slice(1)
                : ""}{" "}
              Module!
            </Typography>
            <Box
              sx={{
                bgcolor: "success.50",
                p: 2,
                borderRadius: 2,
                mb: 2,
                border: "1px solid",
                borderColor: "success.200",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "success.dark" }}
              >
                ✅ All {sections.length} sections completed (
                {completionPercentage}%)
              </Typography>
              <Typography variant="body2" color="success.dark">
                🏆 Ready for final assessment
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Choose your next step to complete your learning journey:
            </Typography>
          </Box>
        ),
        actions: [
          {
            label: "Review Content",
            variant: "outlined",
            startIcon: <Reviews />,
            onClick: handleReviewModule,
          },
          {
            label: "Take Final Quiz",
            variant: "contained",
            startIcon: <Quiz />,
            sx: {
              backgroundColor: "#4A90A4",
              "&:hover": { backgroundColor: "#3A7B8E" },
            },
            onClick: handleTakeQuiz,
          },
        ],
        maxWidth: "md",
        disableBackdropClick: true,
        showCloseButton: false,
      });
    }
  }, [
    completedSections,
    sections,
    moduleContext,
    moduleId,
    showConfirm,
    handleReviewModule,
    handleTakeQuiz,
  ]);

  // ============================================================
  // Computed Values
  // ============================================================

  const currentSectionData = sections.find((s) => s.id === currentSection);
  const currentSectionIndex = sections.findIndex(
    (s) => s.id === currentSection
  );
  const CurrentComponent = currentSectionData?.component;
  const nextSection = sections[currentSectionIndex + 1];
  const isLastSection = currentSectionIndex === sections.length - 1;

  const allSectionsCompleted = completedSections.size === sections.length;
  const completionPercentage =
    sections.length > 0
      ? Math.round((completedSections.size / sections.length) * 100)
      : 0;

  // Dynamic button configuration
  const getCompletionButtonConfig = () => {
    if (allSectionsCompleted) {
      return {
        text: "Complete Module",
        icon: <EmojiEvents />,
        color: "success" as const,
        variant: "contained" as const,
        sx: {
          backgroundColor: "#4CAF50",
          "&:hover": { backgroundColor: "#45A049" },
          fontWeight: 600,
        },
      };
    } else {
      return {
        text: `Continue Learning (${completionPercentage}%)`,
        icon: <School />,
        color: "primary" as const,
        variant: "contained" as const,
        sx: {
          backgroundColor: "#4A90A4",
          "&:hover": { backgroundColor: "#3A7B8E" },
        },
      };
    }
  };

  const buttonConfig = getCompletionButtonConfig();

  // ============================================================
  // Render States
  // ============================================================

  // Loading state
  if (loading) {
    return (
      <DocumentContainer>
        <DocumentHeader>
          <HeaderTitle>Loading Module...</HeaderTitle>
        </DocumentHeader>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Typography>Loading module content...</Typography>
        </Box>
      </DocumentContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <DocumentContainer>
        <DocumentHeader>
          <HeaderTitle>Module Error</HeaderTitle>
        </DocumentHeader>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Failed to Load Module</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          {moduleId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Module ID: {moduleId}
              </Typography>
              {moduleContext && (
                <Typography variant="body2" color="text.secondary">
                  Parsed Context: {moduleContext.program.toUpperCase()}{" "}
                  {moduleContext.program.toUpperCase()} / {moduleContext.week} /{" "}
                  {moduleContext.moduleType}
                </Typography>
              )}
            </Box>
          )}
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </DocumentContainer>
    );
  }

  // No sections loaded
  if (!sections.length) {
    return (
      <DocumentContainer>
        <DocumentHeader>
          <HeaderTitle>{moduleContext?.displayName || "Module"}</HeaderTitle>
        </DocumentHeader>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            <Typography>
              No content sections available for this module.
            </Typography>
          </Alert>
        </Box>
      </DocumentContainer>
    );
  }

  // Show quiz if activated
  if (showQuiz && moduleContext) {
    const quizModuleId = `eap_${moduleContext.program}_${
      moduleContext.week
    }_module${
      moduleContext.moduleType === "reading"
        ? "1"
        : moduleContext.moduleType === "listening"
        ? "2"
        : moduleContext.moduleType === "speaking"
        ? "3"
        : moduleContext.moduleType === "writing"
        ? "4"
        : moduleContext.moduleType === "grammar"
        ? "5"
        : moduleContext.moduleType === "vocabulary"
        ? "6"
        : moduleContext.moduleType === "review"
        ? "7"
        : "1"
    }`;

    return (
      <QuizStartScreen
        moduleId={quizModuleId}
        moduleName={`${
          moduleContext.moduleType.charAt(0).toUpperCase() +
          moduleContext.moduleType.slice(1)
        } Module`}
        week={parseInt(moduleContext.week.replace("week", ""))}
        onComplete={handleQuizComplete}
        onExit={handleQuizExit}
      />
    );
  }

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <DocumentContainer>
      {/* Document Header */}
      <DocumentHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <HeaderTitle>{moduleContext?.displayName || "Module"}</HeaderTitle>
        </Box>

        <HeaderActions>
          {/* Show Take Quiz button when all sections completed */}
          {allSectionsCompleted && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Quiz />}
              onClick={handleTakeQuiz}
              disabled={isSaving}
              sx={{
                backgroundColor: "#4A90A4",
                "&:hover": { backgroundColor: "#3A7B8E" },
                mr: 1,
              }}
            >
              {isSaving ? "Saving..." : "Take Final Quiz"}
            </Button>
          )}

          <Button
            variant={buttonConfig.variant}
            color={buttonConfig.color}
            size="small"
            startIcon={buttonConfig.icon}
            onClick={handleCompleteModule}
            sx={buttonConfig.sx}
          >
            {buttonConfig.text}
          </Button>
        </HeaderActions>
      </DocumentHeader>

      {/* Document Content */}
      <DocumentContent>
        {/* Sidebar */}
        {!isMobile && (
          <DocumentSidebar>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontSize: "16px", fontWeight: 600 }}
            >
              Module Contents
            </Typography>

            {sections.map((section, index) => {
              const isActive = currentSection === section.id;
              const isCompleted = completedSections.has(section.id);
              const isLocked = isSectionLocked(section.id, index);

              return (
                <ModuleNavItem
                  key={section.id}
                  isActive={isActive}
                  isCompleted={isCompleted}
                  isLocked={isLocked}
                  onClick={() => handleSectionClick(section.id, index)}
                >
                  <ModuleNumber
                    isActive={isActive}
                    isCompleted={isCompleted}
                    isLocked={isLocked}
                  >
                    {isLocked ? (
                      <Lock sx={{ fontSize: 14 }} />
                    ) : isCompleted ? (
                      <Check sx={{ fontSize: 16, fontWeight: "bold" }} />
                    ) : (
                      index + 1
                    )}
                  </ModuleNumber>

                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {section.config?.title || section.id}
                  </Typography>
                </ModuleNavItem>
              );
            })}

            <Box sx={{ mt: 3 }}>
              <Typography
                variant="body2"
                sx={{ color: "#6C757D", fontSize: "12px", mb: 1 }}
              >
                Progress: {completionPercentage}% Complete
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: allSectionsCompleted
                      ? "#4CAF50"
                      : "#4A90A4",
                  },
                }}
              />
            </Box>

            {allSectionsCompleted ? (
              <Alert severity="success" sx={{ mt: 2, fontSize: "12px" }}>
                🎉 Ready for final quiz!
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 2, fontSize: "12px" }}>
                📚 {sections.length - completedSections.size} sections remaining
              </Alert>
            )}
          </DocumentSidebar>
        )}

        {/* Mobile Navigation */}
        {isMobile && (
          <DocumentSidebar>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontSize: "16px", fontWeight: 600 }}
            >
              Section {currentSectionIndex + 1} of {sections.length}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#6C757D", mb: 2 }}>
              {currentSectionData?.config?.title || currentSectionData?.id}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((currentSectionIndex + 1) / sections.length) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </DocumentSidebar>
        )}

        {/* Main Content */}
        <DocumentMain>
          <ContentArea>
            {CurrentComponent && (
              <CurrentComponent
                onComplete={() => handleSectionComplete(currentSection)}
                isLocked={isSectionLocked(currentSection, currentSectionIndex)}
                nextSectionTitle={
                  isLastSection
                    ? "Complete Module"
                    : nextSection?.config?.title ||
                      nextSection?.id ||
                      "Next Section"
                }
                onGoToNextSection={
                  !isLastSection ? handleGoToNextSection : undefined
                }
                isLastSection={isLastSection}
                sectionId={currentSection}
              />
            )}
          </ContentArea>
        </DocumentMain>
      </DocumentContent>

      {/* Mobile bottom actions */}
      {isMobile && (
        <Box
          sx={{
            borderTop: "1px solid #E9ECEF",
            padding: "12px 16px",
            backgroundColor: "#F8F9FA",
            display: "flex",
            gap: 1,
          }}
        >
          {allSectionsCompleted ? (
            <Button
              variant="contained"
              fullWidth
              startIcon={<Quiz />}
              onClick={handleTakeQuiz}
              disabled={isSaving}
              sx={{
                backgroundColor: "#4A90A4",
                "&:hover": { backgroundColor: "#3A7B8E" },
                py: 1.5,
              }}
            >
              {isSaving ? "Saving..." : "Take Final Quiz"}
            </Button>
          ) : (
            <Button
              variant={buttonConfig.variant}
              color={buttonConfig.color}
              fullWidth
              startIcon={buttonConfig.icon}
              onClick={handleCompleteModule}
              sx={{
                ...buttonConfig.sx,
                py: 1.5,
              }}
            >
              {buttonConfig.text}
            </Button>
          )}
        </Box>
      )}

      {/* Render the dialog */}
      <ConfirmDialog />
    </DocumentContainer>
  );
};

export default ModulePage;
